import { Database } from 'types/supabase'

/**
 * The beat area picker (D-01, D-02): one control that picks either a
 * municipality or one town, encoded as a single Select value and decoded into
 * exactly one of import_beats.area_city_id / area_municipality — the shape the
 * DB enforces with import_beats_area_exactly_one and the
 * import_beats_area_municipality_known trigger
 * (mobile/supabase/migrations/20240101000028_newsroom_intake_and_beat_area.sql).
 *
 * Pure: no Supabase call and no translation lookup here. The page passes in the cities from
 * listCities() and the translated labels.
 */

type CityRow = Database['public']['Tables']['cities']['Row']

/** The cities columns the picker needs; a full CityRow from listCities() fits. */
export type AreaCity = Pick<CityRow, 'id' | 'name' | 'municipality'>

export type BeatArea = {
  area_city_id: number | null
  area_municipality: string | null
}

export type AreaLabels = {
  municipalitiesGroup: string
  townsGroup: string
  municipalitySuffix: string
}

export type AreaOption = { value: string; label: string }

/** Mantine 7 grouped Select data. */
export type AreaOptionGroup = { group: string; items: AreaOption[] }

const MUNICIPALITY_PREFIX = 'municipality:'
const CITY_PREFIX = 'city:'
const POSITIVE_INTEGER = /^[1-9][0-9]*$/

const byLabel = (a: AreaOption, b: AreaOption) => a.label.localeCompare(b.label, 'pt')

const municipalityLabel = (municipality: string, labels: AreaLabels) =>
  `${municipality} ${labels.municipalitySuffix}`

const townLabel = (city: AreaCity) =>
  city.municipality ? `${city.name} (${city.municipality})` : city.name

const isPositiveInteger = (n: number) => Number.isSafeInteger(n) && n > 0

export const buildAreaOptions = (
  cities: AreaCity[],
  labels: AreaLabels
): AreaOptionGroup[] => {
  const municipalities = new Set<string>()
  for (const city of cities) {
    if (city.municipality) municipalities.add(city.municipality)
  }

  const municipalityItems = Array.from(municipalities)
    .map((m) => ({ value: `${MUNICIPALITY_PREFIX}${m}`, label: municipalityLabel(m, labels) }))
    .sort(byLabel)

  const townItems = cities
    .map((city) => ({ value: `${CITY_PREFIX}${city.id}`, label: townLabel(city) }))
    .sort(byLabel)

  return [
    { group: labels.municipalitiesGroup, items: municipalityItems },
    { group: labels.townsGroup, items: townItems }
  ]
}

/** The Select value for an area, or null unless exactly one field is set. */
export const encodeArea = (area: BeatArea): string | null => {
  const hasCity = area.area_city_id !== null
  const hasMunicipality = area.area_municipality !== null
  if (hasCity === hasMunicipality) return null

  if (hasMunicipality) {
    const municipality = area.area_municipality as string
    if (municipality.trim() === '') return null
    return `${MUNICIPALITY_PREFIX}${municipality}`
  }

  const cityId = area.area_city_id as number
  if (!isPositiveInteger(cityId)) return null
  return `${CITY_PREFIX}${cityId}`
}

/**
 * Decode a Select value into exactly one area field (D-02). Accepts only
 * `municipality:<non-empty>` or `city:<positive integer>`; anything else is null.
 */
export const decodeArea = (value: string | null): BeatArea | null => {
  if (!value) return null

  if (value.startsWith(MUNICIPALITY_PREFIX)) {
    const municipality = value.slice(MUNICIPALITY_PREFIX.length)
    if (municipality.trim() === '') return null
    return { area_city_id: null, area_municipality: municipality }
  }

  if (value.startsWith(CITY_PREFIX)) {
    const raw = value.slice(CITY_PREFIX.length)
    if (!POSITIVE_INTEGER.test(raw)) return null
    const cityId = Number(raw)
    if (!isPositiveInteger(cityId)) return null
    return { area_city_id: cityId, area_municipality: null }
  }

  return null
}

/** The label of a beat's area for the list, or '' when it cannot be named. */
export const formatArea = (
  area: BeatArea,
  cities: AreaCity[],
  labels: AreaLabels
): string => {
  if (encodeArea(area) === null) return ''

  if (area.area_municipality !== null) {
    return municipalityLabel(area.area_municipality, labels)
  }

  const city = cities.find((c) => c.id === area.area_city_id)
  return city ? townLabel(city) : ''
}
